import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';

/**
 * Admin 官方产品模板 · 批量移动站点（多选）弹窗
 */
export default function AdminSheinOfficialBatchMoveSitesModal({
  open,
  selectedCount = 0,
  sites = [],
  busy = false,
  onClose,
  onConfirm,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState('');

  const options = useMemo(
    () => (Array.isArray(sites) ? sites : []).filter((row) => row?.siteAbbr),
    [sites],
  );

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const toggle = (abbr) => {
    const key = String(abbr);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setError('');
  };

  const submit = async (event) => {
    event?.preventDefault?.();
    if (busy) return;
    const sitesSelected = [...selected];
    if (!sitesSelected.length) {
      setError('请至少选择一个站点');
      return;
    }
    await onConfirm?.(sitesSelected);
  };

  return createPortal(
    <div className="spo-batch-modal spo-size-tpl-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label="关闭"
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-size-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-shein-batch-move-sites-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-batch-modal__head">
          <h2 id="admin-shein-batch-move-sites-title">批量移动站点</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose} disabled={busy}>
            关闭
          </button>
        </div>
        <form className="spo-batch-modal__body" onSubmit={submit}>
          <p className="admin-desc" style={{ marginTop: 0 }}>
            将选中的 {selectedCount} 条产品模板的上架站点设置为下方勾选站点。
          </p>
          <div className="spo-field spo-field--full">
            <span className="spo-field__label">
              目标站点 <RequiredMark />
            </span>
            {options.length === 0 ? (
              <p className="admin-desc">暂无可售站点，请先同步店铺站点。</p>
            ) : (
              <div className="admin-shein-batch-move-sites">
                {options.map((row) => {
                  const abbr = String(row.siteAbbr);
                  const label = row.siteName && row.siteName !== '—'
                    ? `${row.siteName}（${abbr}）`
                    : abbr;
                  const checked = selected.has(abbr);
                  return (
                    <label key={abbr} className="admin-shein-batch-move-sites__item">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => toggle(abbr)}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {error ? <span className="spo-field__error">{error}</span> : null}
          </div>
        </form>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost" onClick={onClose} disabled={busy}>
            取消
          </button>
          <button type="button" className="spo-btn spo-btn--primary" onClick={submit} disabled={busy || options.length === 0}>
            {busy ? '迁移中…' : '确认迁移'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
